// i18next-extract-mark-ns-start network-tokenization

//Payment methods
import {AnchorLink} from 'components/AnchorLink';
import {Background} from 'components/Background';
import {Clients} from 'components/Clients';
import {ContactSalesButton} from 'components/ContactSalesButton';
import {Content} from 'components/Content';
import {Section, SectionActions, SectionHeader, SectionImage} from 'components/Section';
import {SEO} from 'components/SEO';
import {SignUpButton} from 'components/SignUpButton';
import {graphql, PageProps} from 'gatsby';
import {Trans, useI18next} from 'gatsby-plugin-react-i18next';

//Images
import password from 'images/password.svg';
import trendgraph from 'images/trendgraph.svg';
import modular from 'images/modular.svg';
import cards from 'images/cards.svg';
import index1 from 'images/payment_gateway.svg';

// Clients

import React from 'react';
import styled from 'styled-components';
import {BlogLink} from 'components/links/Blog';
import {GoogleReviews, Place} from 'components/GoogleReviews';
import {List} from 'components/List';
import {ScreenSizes} from '../../../types/responsive';

const IndexBackground = styled.div`
  position: relative;

  &:before {
    display: block;
    content: '';
    position: absolute;
    transform: rotate(-58deg);
    left: 70%;
    top: 0;
    height: 770px;
    width: 1150px;
    z-index: -1;
    background: #7984d9;
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const IndexContent = styled.div`
  padding-top: 100px;
  padding-bottom: 0;
  max-width: 550px;
  min-height: 600px;
  @media (max-width: ${ScreenSizes.md}) {
    max-width: 500px;
    min-height: 510px;
  }
  @media (max-width: ${ScreenSizes.sm}) {
    max-width: 100%;
    min-height: 410px;
  }
`;

const ReviewsBackground = styled.div`
  position: relative;
  margin-top: 100px;

  &:before {
    display: block;
    content: '';
    z-index: -1;
    height: 70%;
    position: absolute;
    transform: skew(0, -9deg);
    left: 0;
    right: 0;
    bottom: 0;
    top: 20%;
    background: linear-gradient(96.34deg, #00e9d5 0%, #7984d9 100%);
  }
`;

const IndexImage = styled.img`
  position: absolute;
  left: 56%;
  top: 20px;
  width: 540px;
  z-index: 9;

  @media (max-width: 1024px) {
    left: 60%;
    width: 45%;
    top: 1%;
  }
`;

const NetworkTokenization: React.FC<PageProps> = (props) => {
  const {t} = useI18next();
  const {data} = props;
  const place: Place = {
    rating: data.googlePlacesPlace.rating,
    user_ratings_total: data.googlePlacesPlace.user_ratings_total,
    reviews: data.allGooglePlacesReview.nodes
  };
  return (
    <>
      <SEO
        path="network-tokenization"
        title={t('Network Tokenization')}
        description={t(
          'Network tokenization is a way to process payments without exposing card details, raising online payment security and conversion rates.'
        )}
      />
      <IndexBackground>
        <Content>
          <IndexContent>
            <SectionHeader underline tagName="h1">
              <Trans>Unlock the power of network tokenization</Trans>
            </SectionHeader>
            <Trans parent="p">
              The fraud rate for card-not-present (CNP) transactions is 10 times higher than for
              card-present purchases. Mitigate this with network tokenization to improve online
              payment security and authorization rates.
            </Trans>
            <SectionActions>
              <SignUpButton variant="light" style={{marginRight: 15}}>
                <Trans>Open an Account</Trans>
              </SignUpButton>
              <ContactSalesButton />
            </SectionActions>
          </IndexContent>
        </Content>
        <IndexImage
          src={index1}
          alt="Network Tokenization feature screenshot"
          title="Network Tokenization feature screenshot"
          className="hide-on-mobile"
        />
      </IndexBackground>
      <Background>
        <Content>
          <Section>
            <SectionImage
              sx={{display: 'flex', alignItems: 'center'}}
              src={password}
              alt="Network Tokenization feature screenshot"
              title="Network Tokenization feature screenshot"
              width={300}
              mobileWidth={220}
            />
            <div>
              <SectionHeader>
                <Trans>What is network tokenization? </Trans>
              </SectionHeader>
              <Trans parent="p">
                Network tokenization or scheme tokenization is a method supported by major card
                networks like Visa, Mastercard, and American Express that transforms the{' '}
                <BlogLink slug="pan-number">primary account number (PAN)</BlogLink> into a secure
                token to process payments without exposing the actual card details.
              </Trans>
            </div>
          </Section>
          <Section reverseOnMobile>
            <div>
              <SectionHeader>
                <Trans>Benefits of network tokenization</Trans>
              </SectionHeader>
              <List>
                <Trans parent="li">
                  <strong>Enhanced security.</strong>{' '}
                  <BlogLink slug="what-is-tokenization-and-its-benefits-for-e-commerce">
                    Tokenization
                  </BlogLink>{' '}
                  eliminates the need to store sensitive card information, making it significantly
                  harder for fraudsters to steal data.
                </Trans>
                <Trans parent="li">
                  <strong>Improved acceptance rates.</strong> Since tokens are issued by the card
                  schemes themselves, transactions are more likely to be accepted.
                </Trans>
                <Trans parent="li">
                  <strong>Seamless card updates.</strong> Tokens automatically follow card updates
                  issued by Visa and Mastercard, ensuring uninterrupted service for subscriptions
                  and recurring payments without requiring users to update their card information.
                </Trans>
              </List>
            </div>
            <SectionImage
              sx={{display: 'flex', alignItems: 'center'}}
              src={trendgraph}
              alt="Network Tokenization feature screenshot"
              title="Network Tokenization feature screenshot"
              width={300}
              mobileWidth={220}
            />
          </Section>
          <Section>
            <SectionImage
              sx={{display: 'flex', alignItems: 'center'}}
              src={modular}
              alt="Network Tokenization feature screenshot"
              title="Network Tokenization feature screenshot"
              width={350}
              mobileWidth={220}
            />
            <div>
              <SectionHeader>
                <Trans>How network tokenization works</Trans>
              </SectionHeader>
              <Trans parent="p">
                MONEI integrates with leading network tokenization service providers to manage
                tokens on your behalf.
              </Trans>
              <Trans parent="p">
                When you process a transaction, our system uses these tokens to ensure secure and
                efficient payment handling.{' '}
              </Trans>
              <Trans parent="p">
                Whether you’re storing tokens for future transactions or{' '}
                <BlogLink slug="what-is-credit-card-processing">processing payments</BlogLink> in
                real time, MONEI has you covered.
              </Trans>
            </div>
          </Section>
          <Section reverseOnMobile>
            <div>
              <SectionHeader>
                <Trans>Network tokenization vs MONEI tokenization</Trans>
              </SectionHeader>
              <Trans parent="p">
                Network tokenization is different from{' '}
                <AnchorLink href="https://docs.monei.com/docs/guides/save-payment-method/">
                  MONEI tokenization
                </AnchorLink>
              </Trans>
              <List>
                <Trans parent="li">
                  With MONEI tokenization, we swap card details for a payment token so you can save
                  a payment method for the future.
                </Trans>
                <Trans parent="li">
                  With network tokenization, card schemes (networks) create a network token for a
                  card. MONEI stores this network token and then uses it in place of the PAN during
                  the authorization. MONEI takes care of automatically swapping card details for a
                  network token when necessary, without disrupting your regular payment flow.
                </Trans>
              </List>
              <Trans parent="p">
                We automatically use both features together to help increase security and
                authorization rates.
              </Trans>
            </div>
            <SectionImage
              sx={{display: 'flex', alignItems: 'center'}}
              src={cards}
              alt="Network Tokenization feature screenshot"
              title="Network Tokenization feature screenshot"
              width={350}
              mobileWidth={220}
            />
          </Section>
        </Content>
      </Background>
      <ReviewsBackground>
        <div style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
          <SectionHeader underline sx={{textAlign: 'center'}}>
            <Trans>What MONEI merchants are saying</Trans>
          </SectionHeader>
        </div>
        <GoogleReviews place={place} />
      </ReviewsBackground>
      <Content>
        <Section textAlign="center">
          <div>
            <SectionHeader underline tagName="h3">
              <Trans>Proudly used by more than 5000 stores</Trans>
            </SectionHeader>
            <Clients />
          </div>
        </Section>
      </Content>
    </>
  );
};

export default NetworkTokenization;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(
      filter: {ns: {in: ["common", "network-tokenization"]}, language: {eq: $language}}
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    googlePlacesPlace {
      user_ratings_total
      rating
    }
    allGooglePlacesReview(sort: {fields: rating, order: DESC}, filter: {rating: {gt: 2}}) {
      nodes {
        id
        rating
        text
        relative_time_description
        profile_photo_url
        author_name
      }
    }
  }
`;
